import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  Campaign,
  CampaignProjection,
  CampaignWhatIfQuery,
  OrderTypeWaterfall,
  ProjectionVerdict,
} from '@clarity/contract';
import { PLATFORMS } from '@clarity/contract';
import { dec, money, pct, signed } from '../format.js';

/**
 * ProfitModel (clarity-campaign-matching task 13.2, Req 9.3) — the forward
 * profit model of the CampaignDetail surface, rebuilt from the agreed mock
 * `brand/campaigns-mock.html` (the "Will it pay off?" panel) in the teal-glass
 * world. The mock is the contract: the what-if sliders, the Plus / Non-Plus
 * waterfall cards, and the blended verdict are reproduced here as real
 * components over the typed projection.
 *
 * Everything it draws comes off the server-computed {@link CampaignProjection}
 * (`projectCampaign`) carried on the detail response:
 *  - `waterfalls` — the per-order-type waterfall cards (each bucket's lines
 *    `start → neg… → total`, with the net profit per order);
 *  - `blendedProfit` + `verdict` — the mix-weighted net profit per order and its
 *    WIN / BREAK-EVEN / LOSS classification;
 *  - the sliders drive the parent's what-if state (`onWhatIf`), which re-queries
 *    `useCampaign(..., whatIf)` so the SAME pure core recomputes the projection
 *    server-side.
 *
 * The what-if is strictly `discountPct`/`cap` (discount campaigns) or `cpc`
 * (ads). **Co-funding is NOT a slider — it is pinned (Req 7.3)**; it surfaces as
 * a fixed term in the verdict and the provenance strip, never a control.
 */

/** The what-if the sliders own — exactly the three what-if query keys. */
export interface WhatIf {
  discountPct: number;
  cap: number;
  cpc: number;
}

/** The campaign's DECLARED terms = the reset target + the slider defaults. */
export function campaignDefaults(campaign: Campaign): WhatIf {
  return {
    discountPct: campaign.discountPct ?? 0.5,
    cap: campaign.maxDiscountCap ?? 3,
    cpc: campaign.cpc ?? 0.25,
  };
}

/** Fold the live what-if into the query shape, dropping the keys this campaign
 *  does not drive (an ads campaign sends `cpc`; a discount one `discountPct`+`cap`).
 *  Only non-default keys ride, so a pristine model is the plain default projection. */
export function toWhatIfQuery(
  campaign: Campaign,
  whatIf: WhatIf,
): CampaignWhatIfQuery | undefined {
  const def = campaignDefaults(campaign);
  const q: CampaignWhatIfQuery = {};
  if (isAds(campaign)) {
    if (!near(whatIf.cpc, def.cpc)) q.cpc = whatIf.cpc;
  } else {
    if (!near(whatIf.discountPct, def.discountPct)) q.discountPct = whatIf.discountPct;
    if (!near(whatIf.cap, def.cap)) q.cap = whatIf.cap;
  }
  return Object.keys(q).length > 0 ? q : undefined;
}

export function isAds(campaign: Campaign): boolean {
  return campaign.source === 'ads' || campaign.pricingModel !== null;
}

function near(a: number, b: number): boolean {
  return Math.abs(a - b) < 1e-9;
}

const VERDICT_LABEL: Record<ProjectionVerdict, string> = {
  win: 'WIN',
  even: 'BREAK-EVEN',
  lose: 'LOSS',
};

const BUCKET_LABEL: Record<'plus' | 'nonplus', string> = {
  plus: 'Plus (TPRO)',
  nonplus: 'Non-Plus',
};

export function ProfitModel({
  campaign,
  projection,
  whatIf,
  onWhatIf,
  currency,
  recomputing,
}: {
  campaign: Campaign;
  projection: CampaignProjection;
  whatIf: WhatIf;
  onWhatIf: (next: WhatIf) => void;
  currency: string;
  /** True while a slider-driven re-query is in flight — fades the model. */
  recomputing?: boolean;
}) {
  const ads = isAds(campaign);
  const def = campaignDefaults(campaign);
  const dirty = ads
    ? !near(whatIf.cpc, def.cpc)
    : !near(whatIf.discountPct, def.discountPct) || !near(whatIf.cap, def.cap);

  // Ranged offer: the what-if discount slider is constrained to the DECLARED
  // band so it only tests discounts the deal actually allows (the forward model
  // stays a point model on the chosen value). A point offer keeps the open
  // 10–60% exploration range.
  const ranged =
    campaign.discountPctMin !== null &&
    campaign.discountPctMax !== null &&
    campaign.discountPctMax > campaign.discountPctMin;
  const discMin = ranged ? campaign.discountPctMin! : 0.1;
  const discMax = ranged ? campaign.discountPctMax! : 0.6;
  const discLabel = ranged
    ? `Discount % (within ${pct(Math.round(discMin * 100))}–${pct(Math.round(discMax * 100))} band)`
    : 'Discount %';

  const reset = () => onWhatIf({ ...def });

  return (
    <div className="pm">
      <WhatIfControls
        ads={ads}
        whatIf={whatIf}
        defaults={def}
        discMin={discMin}
        discMax={discMax}
        discLabel={discLabel}
        onWhatIf={onWhatIf}
        dirty={dirty}
        onReset={reset}
      />

      <div className={`pm__grid${recomputing ? ' pm__grid--recomputing' : ''}`} aria-busy={recomputing}>
        {(['plus', 'nonplus'] as const).map((bucket) => (
          <WaterfallCard
            key={bucket}
            bucket={bucket}
            waterfall={projection.waterfalls.find((w) => w.bucket === bucket) ?? null}
            ads={ads}
            currency={currency}
          />
        ))}
        <Verdict
          campaign={campaign}
          projection={projection}
          whatIf={whatIf}
          ads={ads}
          currency={currency}
        />
      </div>
    </div>
  );
}

// ── What-if sliders ───────────────────────────────────────────────────────────

/**
 * The what-if sliders (Req 9.3). Discount campaigns get Discount % + Max cap;
 * ads campaigns get Cost per click. Each slider updates a LOCAL value instantly
 * (so the thumb tracks the drag) and debounces the committed what-if up to the
 * parent, which re-queries the projection server-side — reproducing the mock's
 * live slider interaction without a round-trip on every pixel of the drag.
 *
 * Co-funding is deliberately absent: it is a pinned deal term, surfaced in the
 * verdict + provenance strip, never a slider.
 */
function WhatIfControls({
  ads,
  whatIf,
  defaults,
  discMin,
  discMax,
  discLabel,
  onWhatIf,
  dirty,
  onReset,
}: {
  ads: boolean;
  whatIf: WhatIf;
  defaults: WhatIf;
  /** Discount slider bounds — the declared band for a ranged offer, else 0.1–0.6. */
  discMin: number;
  discMax: number;
  /** Discount slider label — annotated with the band for a ranged offer. */
  discLabel: string;
  onWhatIf: (next: WhatIf) => void;
  dirty: boolean;
  onReset: () => void;
}) {
  return (
    <div className="pm__whatif" role="group" aria-label="What-if controls">
      {ads ? (
        <Slider
          id="whatif-cpc"
          label="Cost per click (JOD)"
          min={0.05}
          max={0.6}
          step={0.01}
          value={whatIf.cpc}
          defaultValue={defaults.cpc}
          format={(v) => dec(v)}
          onCommit={(v) => onWhatIf({ ...whatIf, cpc: v })}
        />
      ) : (
        <>
          <Slider
            id="whatif-discount"
            label={discLabel}
            min={discMin}
            max={discMax}
            step={0.05}
            value={whatIf.discountPct}
            defaultValue={defaults.discountPct}
            format={(v) => pct(Math.round(v * 100))}
            onCommit={(v) => onWhatIf({ ...whatIf, discountPct: v })}
          />
          <Slider
            id="whatif-cap"
            label="Max cap (JOD)"
            min={1}
            max={6}
            step={0.25}
            value={whatIf.cap}
            defaultValue={defaults.cap}
            format={(v) => dec(v)}
            onCommit={(v) => onWhatIf({ ...whatIf, cap: v })}
          />
        </>
      )}
      {dirty && (
        <button type="button" className="pm__reset" onClick={onReset}>
          Reset to campaign defaults
        </button>
      )}
    </div>
  );
}

const DEBOUNCE_MS = 180;

/**
 * One accessible range slider. The visible value tracks the drag instantly
 * (local state), while the committed value is debounced to the parent so the
 * server re-query fires once the owner pauses, not on every input event. A
 * controlled `value` prop that diverges from the local draft (an external reset)
 * re-syncs the thumb.
 */
function Slider({
  id,
  label,
  min,
  max,
  step,
  value,
  defaultValue,
  format,
  onCommit,
}: {
  id: string;
  label: string;
  min: number;
  max: number;
  step: number;
  value: number;
  defaultValue: number;
  format: (v: number) => string;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState(value);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-sync when the controlled value changes from outside (e.g. a reset) and
  // isn't just echoing our own in-flight draft.
  useEffect(() => {
    if (!near(value, draft)) setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const commitDebounced = useCallback(
    (v: number) => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => onCommit(v), DEBOUNCE_MS);
    },
    [onCommit],
  );

  const dirty = !near(draft, defaultValue);

  return (
    <div className="wi">
      <div className="wi__top">
        <label htmlFor={id} className="wi__label">
          {label}
          {dirty && <span className="wi__dirty"> · changed</span>}
        </label>
        <span className="wi__val num" aria-hidden="true">
          {format(draft)}
        </span>
      </div>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={draft}
        aria-valuetext={format(draft)}
        onChange={(e) => {
          const v = Number.parseFloat(e.target.value);
          setDraft(v);
          commitDebounced(v);
        }}
      />
    </div>
  );
}

// ── Waterfall cards ─────────────────────────────────────────────────────────

/**
 * One order-type waterfall card (Plus / Non-Plus). Renders the bucket's ordered
 * lines — `start` (customer pays) → `neg` lines (discount, commission, fees,
 * food cost) → `total` (net profit) — and the net profit per order. A bucket the
 * campaign's audience excludes (no waterfall in the projection) reads as "Not
 * targeted", mirroring the mock's dimmed excluded card.
 */
function WaterfallCard({
  bucket,
  waterfall,
  ads,
  currency,
}: {
  bucket: 'plus' | 'nonplus';
  waterfall: OrderTypeWaterfall | null;
  ads: boolean;
  currency: string;
}) {
  if (!waterfall) {
    return (
      <div className="ptype ptype--off">
        <div className="ptype__head">
          <span className="ptype__name">{BUCKET_LABEL[bucket]}</span>
        </div>
        <p className="ptype__excluded">Not targeted by this campaign.</p>
      </div>
    );
  }

  const mix = Math.round(waterfall.mixShare * 100);
  const lines = waterfall.lines.filter(
    // Ads campaigns carry no discount line; the projection already omits it, but
    // guard a zero self-discount line so the card stays discount-free for ads.
    (l) => !(ads && l.key === 'selfDiscount'),
  );
  const net = waterfall.profit;

  return (
    <div className="ptype">
      <div className="ptype__head">
        <span className="ptype__name">{BUCKET_LABEL[bucket]}</span>
        <span className="ptype__mix">{pct(mix)} of orders</span>
      </div>
      <div className="wf">
        {lines.map((line) => (
          <div key={line.key} className={`wf__row${line.kind === 'start' ? ' wf__row--start' : ''}`}>
            <span className="wf__label">{line.label}</span>
            <span className={`wf__val num wf__val--${line.kind === 'neg' ? 'neg' : 'pos'}`}>
              {line.kind === 'neg' ? '−' : ''}
              {dec(Math.abs(line.amount))}
            </span>
          </div>
        ))}
      </div>
      <div className="ptype__net">
        <span className="ptype__net-label">Net profit / order</span>
        <span className={`ptype__net-val num${net < 0 ? ' ptype__net-val--neg' : ''}`}>
          {money(net, currency)}
        </span>
      </div>
    </div>
  );
}

// ── Blended verdict ───────────────────────────────────────────────────────────

/**
 * The blended verdict (Req 7.2): the mix-weighted net profit per order, its
 * WIN / BREAK-EVEN / LOSS classification, and the levers in play. Co-funding
 * shows as a FIXED term here (the pinned deal term, Req 7.3) — never a control.
 */
function Verdict({
  campaign,
  projection,
  whatIf,
  ads,
  currency,
}: {
  campaign: Campaign;
  projection: CampaignProjection;
  whatIf: WhatIf;
  ads: boolean;
  currency: string;
}) {
  const blended = projection.blendedProfit;
  const verdict = projection.verdict;
  const mix = projection.waterfalls.map((w) => Math.round(w.mixShare * 100));
  const mixLabel = mix.map((m) => pct(m)).join(' / ');

  const cap = ads ? 'margin / order before ad cost · ' + currency : 'blended profit / order · ' + currency;
  const badgeLabel = ads ? 'see break-even below' : `${VERDICT_LABEL[verdict]} at this discount`;
  const badgeCls = ads ? 'even' : verdict;

  return (
    <div className="pm__verdict" aria-label="Blended verdict">
      <div className="pm__verdict-hero">
        <div className={`pm__verdict-num num${blended < 0 ? ' pm__verdict-num--neg' : ''}`}>
          {signed(blended)}
        </div>
        <div className="pm__verdict-cap">{cap}</div>
      </div>

      <dl className="pm__kv">
        <div className="kv">
          <dt className="kv__k">Order mix</dt>
          <dd className="kv__v num">{mixLabel}</dd>
        </div>
        {ads ? (
          <>
            <div className="kv">
              <dt className="kv__k">Cost per click</dt>
              <dd className="kv__v num">{money(whatIf.cpc, currency)}</dd>
            </div>
            <div className="kv">
              <dt className="kv__k">Decision driver</dt>
              <dd className="kv__v">conversion rate</dd>
            </div>
          </>
        ) : (
          <>
            <div className="kv">
              <dt className="kv__k">{`${PLATFORMS[campaign.platformKey].name} co-funds`}</dt>
              <dd className="kv__v num">{pct(Math.round(campaign.coFundPct * 100))}</dd>
            </div>
            {campaign.discountPctMin !== null &&
              campaign.discountPctMax !== null &&
              campaign.discountPctMax > campaign.discountPctMin && (
                <div className="kv">
                  <dt className="kv__k">Declared range</dt>
                  <dd className="kv__v num">
                    {pct(Math.round(campaign.discountPctMin * 100))}–
                    {pct(Math.round(campaign.discountPctMax * 100))}
                  </dd>
                </div>
              )}
            <div className="kv">
              <dt className="kv__k">
                {campaign.discountPctMax !== null ? 'Testing at' : 'Effective discount'}
              </dt>
              <dd className="kv__v num">
                {pct(Math.round(whatIf.discountPct * 100))} / cap {dec(whatIf.cap)}
              </dd>
            </div>
          </>
        )}
      </dl>

      <span className={`badge pm__verdict-badge pm__verdict-badge--${badgeCls}`}>{badgeLabel}</span>
    </div>
  );
}
